import { candidateService } from '@core/supabase/services/candidateService';
import { parseSavedProfileResumeFile } from '@modules/single-optimize/aiResumeOptimizer/utils/savedProfileResumeFile';
import type { OptimizedResumePreviewModalPayload } from './OptimizedResumePreviewModal';

export type ResumePreviewLoadResult =
  | { mode: 'modal'; payload: OptimizedResumePreviewModalPayload }
  | { mode: 'external'; href: string };

function parseStorageErrorMessage(text: string, status: number): Error {
  const trimmed = text.trim();
  if (/bucket not found/i.test(trimmed) || /"error"\s*:\s*"Bucket not found"/i.test(trimmed)) {
    return new Error(
      'Résumé storage is not configured (bucket missing or wrong name). Create the bucket in Supabase or set VITE_SUPABASE_STORAGE_BUCKET_CANDIDATE_RESUMES to match your project.'
    );
  }
  try {
    const j = JSON.parse(trimmed) as { error?: string; message?: string; statusCode?: string };
    const msg = String(j.message ?? j.error ?? '');
    if (/bucket not found/i.test(msg) || j.error === 'Bucket not found') {
      return new Error(
        'Résumé storage is not configured (bucket missing or wrong name). Create the bucket in Supabase or set VITE_SUPABASE_STORAGE_BUCKET_CANDIDATE_RESUMES to match your project.'
      );
    }
    return new Error(msg || `Could not load résumé (${status})`);
  } catch (e) {
    if (e instanceof Error && e.message.includes('Résumé storage')) return e;
    return new Error(`Could not load résumé (${status})`);
  }
}

/** Reject JSON error bodies (e.g. missing bucket) before showing an iframe or parsing text. */
async function assertResumeResponseOk(href: string): Promise<void> {
  const res = await fetch(href);
  const ct = res.headers.get('content-type') || '';
  if (!res.ok) {
    const text = await res.text();
    throw parseStorageErrorMessage(text, res.status);
  }
  if (ct.includes('application/json')) {
    const text = await res.text();
    throw parseStorageErrorMessage(text, res.status);
  }
}

/** Prefer HEAD for non-PDF files to avoid downloading large binaries before “Open in new tab”. */
async function assertResumeUrlReachable(href: string): Promise<void> {
  let head: Response | null = null;
  try {
    head = await fetch(href, { method: 'HEAD' });
  } catch {
    head = null;
  }
  if (head?.ok) {
    const ct = head.headers.get('content-type') || '';
    if (!ct.includes('application/json')) return;
  }
  const res = await fetch(href);
  const ct = res.headers.get('content-type') || '';
  if (!res.ok) {
    const text = await res.text();
    throw parseStorageErrorMessage(text, res.status);
  }
  if (ct.includes('application/json')) {
    const text = await res.text();
    throw parseStorageErrorMessage(text, res.status);
  }
}

async function resolveResumeHref(path: string): Promise<string> {
  try {
    return await candidateService.getResumeSignedUrl(path);
  } catch {
    return candidateService.getResumeUrl(path);
  }
}

/** Resolve storage path → in-app preview payload, or external tab for non-previewable types. */
export async function loadResumePreviewPayloadFromStoragePath(path: string): Promise<ResumePreviewLoadResult> {
  const href = await resolveResumeHref(path);

  if (/\.pdf$/i.test(path)) {
    await assertResumeResponseOk(href);
    return { mode: 'modal', payload: { kind: 'pdf', url: href } };
  }

  if (/\.txt$/i.test(path)) {
    const res = await fetch(href);
    const bodyText = await res.text();
    if (!res.ok) {
      throw parseStorageErrorMessage(bodyText, res.status);
    }
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      throw parseStorageErrorMessage(bodyText, res.status);
    }
    const raw = bodyText.replace(/^\uFEFF/, '').trim();
    const parsed = parseSavedProfileResumeFile(raw);
    return {
      mode: 'modal',
      payload: {
        kind: 'text',
        body: parsed.body || '(Empty file)',
        templateId: parsed.templateId,
        design: parsed.design,
      },
    };
  }

  await assertResumeUrlReachable(href);
  return { mode: 'external', href };
}
